<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog app-modal__dialog--xl supplier-import-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="supplier-import-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="supplier-import-modal-title">{{ text.imports.modal.title }}</h3>
          <p>{{ text.imports.modal.subtitle }}</p>
        </div>
        <button type="button" class="app-modal__close" @click="emitClose">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__content supplier-import-modal__content">
        <form class="app-modal__form" @submit.prevent="executeImport">
          <div class="supplier-import-modal__form-body">
            <section class="app-modal__section">
              <div class="supplier-import-modal__sections-grid">
                <button
                  v-for="section in sections"
                  :key="section.key"
                  type="button"
                  class="supplier-import-modal-option"
                  :class="{ 'supplier-import-modal-option--active': activeSectionKey === section.key }"
                  @click="setActiveSection(section.key)"
                >
                  <span class="supplier-import-modal-option__checkbox" :class="{ 'is-checked': activeSectionKey === section.key }">
                    <i class="fa fa-check" aria-hidden="true"></i>
                  </span>
                  <span class="supplier-import-modal-option__body">
                    <span class="supplier-import-modal-option__title">{{ section.label }}</span>
                    <span class="supplier-import-modal-option__subtitle">{{ section.subtitle }}</span>
                  </span>
                </button>
              </div>
            </section>

            <section class="app-modal__section supplier-import-modal__upload-block">
              <div class="supplier-import-modal__upload-field">
                <div class="supplier-import-modal__upload-main">
                  <button
                    type="button"
                    class="app-header-btn app-header-btn--secondary supplier-import-modal__file-trigger"
                    :disabled="!activeSectionKey"
                    @click="openFileDialog"
                  >
                    {{ text.imports.modal.upload.chooseFile }}
                  </button>
                  <span class="supplier-import-modal__file-name">{{ currentFileName }}</span>
                  <button
                    v-if="hasCurrentFile"
                    type="button"
                    class="supplier-import-modal__file-clear"
                    :aria-label="text.imports.modal.upload.removeFile"
                    :title="text.imports.modal.upload.removeFile"
                    @click="removeCurrentFile"
                  >
                    <i class="fa fa-trash" aria-hidden="true"></i>
                  </button>
                </div>

                <input
                  ref="fileInput"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  class="supplier-import-modal__file-input"
                  @change="onFileChange"
                >
              </div>

              <div class="supplier-import-modal__upload-meta">
                <small>{{ text.imports.modal.upload.formats }}</small>
                <small>{{ text.imports.modal.upload.sizeHint }}</small>
              </div>
            </section>

            <section class="app-modal__section supplier-import-modal__details-section">
              <details class="supplier-import-modal__details">
                <summary>{{ text.imports.modal.instructions.title }}</summary>
                <p>{{ selectedInstructionsSubtitle }}</p>
                <ul>
                  <li v-for="(instruction, index) in selectedInstructions" :key="index">
                    {{ instruction }}
                  </li>
                </ul>
              </details>
            </section>

            <section class="app-modal__section supplier-import-modal__details-section">
              <details class="supplier-import-modal__details" open>
                <summary>{{ text.imports.modal.visualExample.title }}</summary>
                <p>{{ text.imports.modal.visualExample.subtitle }}</p>

                <div class="app-table-wrap supplier-import-modal__example-wrap">
                  <div class="app-table-scroll">
                    <table class="app-data-table supplier-import-modal__example-table">
                      <thead>
                        <tr>
                          <th v-for="(column, index) in activeExample.columns" :key="`head-${index}`">
                            {{ column }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td v-for="(value, index) in activeExample.row" :key="`row-${index}`">{{ value }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </details>
            </section>

            <section class="app-modal__section supplier-import-modal__messages" v-if="hasResult">
              <h4>{{ resultTitle }}</h4>

              <div
                v-if="resultMode === 'validation'"
                class="supplier-import-modal__info-note"
              >
                {{ validationInfoMessage }}
              </div>

              <div v-if="resultMode === 'validation'" class="supplier-import-modal__summary-grid">
                <article class="supplier-import-modal__summary-card">
                  <small>{{ text.imports.modal.validation.summary.rowsTotal }}</small>
                  <strong>{{ resultSummary.rowsTotal }}</strong>
                </article>
                <article class="supplier-import-modal__summary-card">
                  <small>{{ text.imports.modal.validation.summary.rowsValid }}</small>
                  <strong>{{ resultSummary.rowsValid }}</strong>
                </article>
                <article class="supplier-import-modal__summary-card">
                  <small>{{ text.imports.modal.validation.summary.rowsInvalid }}</small>
                  <strong>{{ resultSummary.rowsInvalid }}</strong>
                </article>
                <article class="supplier-import-modal__summary-card">
                  <small>{{ text.imports.modal.validation.summary.rowsWarnings }}</small>
                  <strong>{{ resultSummary.rowsWarnings }}</strong>
                </article>
              </div>

              <div v-if="resultMode === 'validation'" class="app-table-wrap supplier-import-modal__results-wrap">
                <div class="app-table-scroll">
                  <table class="app-data-table supplier-import-modal__results-table">
                    <thead>
                      <tr>
                        <th>{{ text.imports.modal.validation.table.type }}</th>
                        <th>{{ text.imports.modal.validation.table.row }}</th>
                        <th>{{ text.imports.modal.validation.table.column }}</th>
                        <th>{{ text.imports.modal.validation.table.message }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="resultRows.length <= 0">
                        <td colspan="4">{{ text.imports.modal.validation.table.empty }}</td>
                      </tr>
                      <tr v-for="(message, index) in resultRows" :key="`msg-${index}`" :class="`type-${message.type || 'info'}`">
                        <td><strong>{{ (message.type || 'info').toUpperCase() }}</strong></td>
                        <td>{{ message.row_number || 0 }}</td>
                        <td>{{ message.column_name || '-' }}</td>
                        <td>{{ message.message }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-if="resultMode === 'import'" class="supplier-import-modal__info-note">
                {{ importInfoMessage }}
              </div>

              <div v-if="resultMode === 'import'" class="supplier-import-modal__summary-grid supplier-import-modal__summary-grid--import">
                <article class="supplier-import-modal__summary-card">
                  <small>{{ text.imports.modal.validation.summary.rowsProcessed }}</small>
                  <strong>{{ resultSummary.rowsTotal }}</strong>
                </article>
                <article class="supplier-import-modal__summary-card">
                  <small>{{ text.imports.modal.validation.summary.rowsCreated }}</small>
                  <strong>{{ resultSummary.rowsCreated }}</strong>
                </article>
                <article class="supplier-import-modal__summary-card">
                  <small>{{ text.imports.modal.validation.summary.rowsUpdated }}</small>
                  <strong>{{ resultSummary.rowsUpdated }}</strong>
                </article>
                <article class="supplier-import-modal__summary-card">
                  <small>{{ text.imports.modal.validation.summary.rowsWarnings }}</small>
                  <strong>{{ resultSummary.rowsWarnings }}</strong>
                </article>
              </div>
            </section>
          </div>

          <footer class="app-modal__footer supplier-import-modal__footer">
            <div class="app-modal__footer-right">
              <button type="button" class="btn btn-light" @click="emitClose">{{ text.imports.modal.footer.cancel }}</button>
              <button type="button" class="btn btn-secondary" :disabled="loading || !activeSectionKey" @click="downloadTemplate">
                {{ text.imports.modal.footer.downloadTemplate }}
              </button>
              <button type="button" class="btn btn-success" :disabled="loading || !canValidate" @click="validateFiles">
                {{ loading ? text.imports.modal.footer.validating : text.imports.modal.footer.validate }}
              </button>
              <button type="submit" class="btn btn-primary" :disabled="loading || !canImport">
                {{ loading ? text.imports.modal.footer.importing : text.imports.modal.footer.import }}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </section>
  </div>
</template>

<script>
import suppliersService from '@/services/suppliers';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('suppliers');

export default {
  name: 'SupplierImportModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'success'],
  data() {
    return {
      text,
      sections: [
        {
          key: 'suppliers',
          label: text.imports.modal.sections.suppliers.title,
          subtitle: text.imports.modal.sections.suppliers.subtitle,
        },
        {
          key: 'billing',
          label: text.imports.modal.sections.billing.title,
          subtitle: text.imports.modal.sections.billing.subtitle,
        },
        {
          key: 'people',
          label: text.imports.modal.sections.people.title,
          subtitle: text.imports.modal.sections.people.subtitle,
        },
      ],
      activeSectionKey: 'suppliers',
      files: {},
      messages: [],
      resultMode: '',
      resultSummary: {
        rowsTotal: 0,
        rowsValid: 0,
        rowsInvalid: 0,
        rowsWarnings: 0,
        rowsCreated: 0,
        rowsUpdated: 0,
      },
      loading: false,
      validatedBatchId: 0,
      canImport: false,
    };
  },
  watch: {
    show() {
      this.resetFormState();
    },
  },
  computed: {
    canValidate() {
      if (!this.activeSectionKey) {
        return false;
      }

      return Boolean(this.files[this.activeSectionKey]);
    },
    hasCurrentFile() {
      if (!this.activeSectionKey) {
        return false;
      }

      return Boolean(this.files[this.activeSectionKey]);
    },
    currentFileName() {
      if (!this.activeSectionKey) {
        return this.text.imports.modal.upload.empty;
      }

      return this.files[this.activeSectionKey]?.name || this.text.imports.modal.upload.empty;
    },
    activeExample() {
      const allExamples = this.text.imports.modal.examples || {};
      return allExamples[this.activeSectionKey] || allExamples.suppliers || { columns: [], row: [] };
    },
    hasResult() {
      return this.resultMode !== '';
    },
    resultTitle() {
      if (this.resultMode === 'import') {
        return this.text.imports.modal.validation.importTitle;
      }

      return this.text.imports.modal.validation.title;
    },
    importInfoMessage() {
      return this.text.imports.modal.validation.importSuccessInfo(
        this.resultSummary.rowsTotal,
        this.resultSummary.rowsCreated,
        this.resultSummary.rowsUpdated,
        this.resultSummary.rowsInvalid,
        this.resultSummary.rowsWarnings
      );
    },
    validationInfoMessage() {
      return this.text.imports.modal.validation.validationInfo(
        this.canImport,
        this.resultSummary.rowsInvalid,
        this.resultSummary.rowsWarnings
      );
    },
    resultRows() {
      return Array.isArray(this.messages) ? this.messages : [];
    },
    selectedInstructionsSubtitle() {
      const section = this.sections.find((item) => item.key === this.activeSectionKey);
      if (!section) {
        return this.text.imports.modal.instructions.subtitle;
      }

      return section.subtitle;
    },
    selectedInstructions() {
      const sectionBullets = this.text.imports.modal.instructions.bySection || {};
      return sectionBullets[this.activeSectionKey] || this.text.imports.modal.instructions.bullets || [];
    },
  },
  methods: {
    resetResultState() {
      this.messages = [];
      this.resultMode = '';
      this.resultSummary = {
        rowsTotal: 0,
        rowsValid: 0,
        rowsInvalid: 0,
        rowsWarnings: 0,
        rowsCreated: 0,
        rowsUpdated: 0,
      };
      this.validatedBatchId = 0;
      this.canImport = false;
    },
    clearFileInputElement() {
      const input = this.$refs.fileInput;
      if (input && typeof input.value !== 'undefined') {
        input.value = '';
      }
    },
    resetFormState() {
      this.activeSectionKey = 'suppliers';
      this.files = {};
      this.resetResultState();
      this.clearFileInputElement();
    },
    setActiveSection(sectionKey) {
      if (this.activeSectionKey === sectionKey) {
        return;
      }

      this.activeSectionKey = sectionKey;
      this.files = {};
      this.resetResultState();
      this.clearFileInputElement();
    },
    emitClose() {
      if (this.loading) {
        return;
      }

      this.$emit('close');
    },
    openFileDialog() {
      if (!this.activeSectionKey) {
        return;
      }

      const input = this.$refs.fileInput;
      if (input && typeof input.click === 'function') {
        input.click();
      }
    },
    onFileChange(event) {
      if (!this.activeSectionKey) {
        return;
      }

      const file = event?.target?.files?.[0] || null;
      if (!file) {
        return;
      }

      this.files = {
        ...this.files,
        [this.activeSectionKey]: file,
      };
      this.resetResultState();

      if (event?.target) {
        event.target.value = '';
      }
    },
    removeCurrentFile() {
      if (!this.activeSectionKey) {
        return;
      }

      const nextFiles = { ...this.files };
      delete nextFiles[this.activeSectionKey];
      this.files = nextFiles;
      this.resetResultState();
      this.clearFileInputElement();
    },
    async downloadTemplate() {
      if (!this.activeSectionKey) {
        return;
      }

      try {
        const response = await suppliersService.importTemplate(this.activeSectionKey);
        const blob = response instanceof Blob ? response : new Blob([response]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `plantilla_orderly_importacion_${this.activeSectionKey}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$toast?.error(this.text.imports.modal.toast.templateError);
      }
    },
    async validateFiles() {
      if (!this.activeSectionKey) {
        this.$toast?.error(this.text.imports.modal.sectionRequired);
        return;
      }

      if (!this.canValidate) {
        this.$toast?.error(this.text.imports.modal.fileRequired);
        return;
      }

      this.loading = true;
      this.resetResultState();

      try {
        const formData = new FormData();
        formData.append('sections[]', this.activeSectionKey);
        formData.append(`files[${this.activeSectionKey}]`, this.files[this.activeSectionKey]);

        const response = await suppliersService.importValidate(formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const payload = response || {};
        const data = payload?.data || {};
        const sectionResult = data?.section || null;

        this.validatedBatchId = Number(data.batch_id || 0);
        this.canImport = Boolean(data.can_import);
        this.resultMode = 'validation';
        this.resultSummary = {
          rowsTotal: Number(sectionResult?.rows_total || 0),
          rowsValid: Number(sectionResult?.rows_valid || 0),
          rowsInvalid: Number(sectionResult?.rows_invalid || 0),
          rowsWarnings: 0,
          rowsCreated: 0,
          rowsUpdated: 0,
        };

        const responseRowLog = Array.isArray(sectionResult?.row_log) ? sectionResult.row_log : [];
        this.messages = responseRowLog.flatMap((rowItem) => {
          const rowNumber = Number(rowItem?.row_number || 0);
          const entries = Array.isArray(rowItem?.entries) ? rowItem.entries : [];

          return entries.map((entry) => ({
            type: String(entry?.type || 'info'),
            row_number: rowNumber,
            column_name: String(entry?.column_name || ''),
            message: String(entry?.message || ''),
          }));
        });

        if (this.messages.length <= 0 && this.canImport) {
          this.$toast?.success(this.text.imports.modal.toast.validateSuccess);
        }
      } catch (error) {
        this.$toast?.error(this.text.imports.modal.toast.validateError);
      } finally {
        this.loading = false;
      }
    },
    async executeImport() {
      if (!this.canImport || this.validatedBatchId <= 0) {
        return;
      }

      this.loading = true;
      try {
        const response = await suppliersService.importExecute(this.validatedBatchId);
        const payload = response || {};
        const data = payload?.data || {};
        const summary = data?.summary || {};

        this.resultMode = 'import';
        this.resultSummary = {
          rowsTotal: Number(summary?.rows_total || 0),
          rowsValid: Number((summary?.rows_created || 0) + (summary?.rows_updated || 0)),
          rowsInvalid: Number(summary?.rows_errors || 0),
          rowsWarnings: Number(summary?.rows_warnings || 0),
          rowsCreated: Number(summary?.rows_created || 0),
          rowsUpdated: Number(summary?.rows_updated || 0),
        };
        this.messages = [];

        this.$emit('success');
        this.$toast?.success(this.text.imports.modal.toast.importSuccess);
      } catch (error) {
        this.$toast?.error(this.text.imports.modal.toast.importError);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/domains/app/components/common/entity-import-modal';

@include entity-import-modal('supplier-import-modal');
</style>
